import React, { useEffect, useMemo, useState } from 'react';
import { BarChart3, Trophy } from 'lucide-react';
import { adminApi } from '../../api/admin';

const CATEGORY_OPTIONS = [
  { key: 'global', label: 'Globale' },
  { key: 'basic', label: 'Basic' },
  { key: 'plus', label: 'Plus' },
  { key: 'rare', label: 'Rare' },
  { key: 'epic', label: 'Epiche' },
  { key: 'unique', label: 'Unique' },
];

const PERIOD_OPTIONS = [
  { key: 'first', label: 'Classifica Mensile #1 (GW1-GW12)' },
  { key: 'second', label: 'Classifica Mensile #2 (GW13-GW20)' },
];

const toItDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('it-IT');
};

const formatScore = (value) => Number(value || 0).toFixed(2);
const FIRST_SHARE_REDISTRIBUTION_RATE = 0.2;

const getApiErrorMessage = (err, fallback) => (
  err?.data?.error || err?.message || fallback
);

const countPrizeSlots = (participantCount, winnerRate = 0.15) => (
  participantCount <= 0 ? 0 : Math.ceil(participantCount * winnerRate)
);

const calculateHarmonicPrizeAmounts = (prizePool, ranks, redistributionRate = FIRST_SHARE_REDISTRIBUTION_RATE) => {
  const pool = Number(prizePool || 0);
  if (pool <= 0 || !Array.isArray(ranks) || ranks.length === 0) return [];

  const ranksU = ranks.map((rank, idx) => {
    const normalized = rank != null ? Number(rank) : idx + 1;
    return Math.max(1, Math.floor(normalized));
  });

  const sumInverse = ranksU.reduce((acc, u) => acc + (1 / u), 0);
  if (sumInverse <= 0) return [];

  let amounts = ranksU.map((u) => (pool / sumInverse) * (1 / u));
  const sumAmt = amounts.reduce((s, a) => s + a, 0);

  if (sumAmt > 0 && Math.abs(sumAmt - pool) > 1e-9) {
    const scale = pool / sumAmt;
    amounts = amounts.map((a) => a * scale);
  }

  if (ranksU.length >= 2 && redistributionRate > 0) {
    const targetTake = Math.max(0, amounts[0]) * redistributionRate;
    const takeFromFirst = Math.min(targetTake, Math.max(0, amounts[0]));
    amounts[0] -= takeFromFirst;
    const perOther = takeFromFirst / (ranksU.length - 1);
    for (let i = 1; i < ranksU.length; i += 1) {
      amounts[i] += perOther;
    }
  }

  let rounded = amounts.map((a) => Math.round(a * 100) / 100);
  const paidSum = rounded.reduce((s, a) => s + a, 0);
  const drift = Math.round((pool - paidSum) * 100) / 100;

  if (rounded.length > 0 && Math.abs(drift) >= 0.005) {
    rounded = [...rounded];
    rounded[0] = Math.round((rounded[0] + drift) * 100) / 100;
  }

  return rounded;
};

function rankBadgeClass(rank) {
  if (rank === 1) return 'bg-amber-500/20 text-amber-300 border-amber-500/40';
  if (rank === 2) return 'bg-slate-400/20 text-slate-200 border-slate-400/40';
  if (rank === 3) return 'bg-orange-700/20 text-orange-300 border-orange-600/40';
  return 'bg-white/5 text-gray-300 border-white/10';
}

function RankCell({ rank }) {
  return (
    <span
      className={`inline-flex min-w-[2rem] justify-center rounded-md border px-2 py-0.5 text-xs font-bold ${rankBadgeClass(rank)}`}
    >
      {rank}
    </span>
  );
}

function GlobalRankingTable({ rows, multipliers, prizeByUserId }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left border-b border-gray-600 bg-gray-700/50">
          <th className="px-4 py-3 text-gray-300 font-semibold w-16">#</th>
          <th className="px-4 py-3 text-gray-300 font-semibold">Utente</th>
          <th className="px-4 py-3 text-gray-300 font-semibold text-right">Plus ×{multipliers.plus ?? 1}</th>
          <th className="px-4 py-3 text-gray-300 font-semibold text-right">Rare ×{multipliers.rare ?? 1.25}</th>
          <th className="px-4 py-3 text-gray-300 font-semibold text-right">Epiche ×{multipliers.epic ?? 1.5}</th>
          <th className="px-4 py-3 text-gray-300 font-semibold text-right">Unique ×{multipliers.unique ?? 2}</th>
          <th className="px-4 py-3 text-blue-300 font-semibold text-right">Totale</th>
          <th className="px-4 py-3 text-emerald-300 font-semibold text-right">Premio</th>
        </tr>
      </thead>
      <tbody>
        {rows.length > 0 ? rows.map((row) => (
          <tr
            key={row.user_id}
            className="border-b border-gray-700/80 hover:bg-gray-700/30 transition-colors"
          >
            <td className="px-4 py-3">
              <RankCell rank={row.rank} />
            </td>
            <td className="px-4 py-3 text-white font-medium">{row.username}</td>
            <td className="px-4 py-3 text-gray-300 text-right tabular-nums">{formatScore(row.plus_score)}</td>
            <td className="px-4 py-3 text-gray-300 text-right tabular-nums">{formatScore(row.rare_score)}</td>
            <td className="px-4 py-3 text-gray-300 text-right tabular-nums">{formatScore(row.epic_score)}</td>
            <td className="px-4 py-3 text-gray-300 text-right tabular-nums">{formatScore(row.unique_score)}</td>
            <td className="px-4 py-3 text-blue-300 text-right font-semibold tabular-nums">
              {formatScore(row.weighted_score)}
            </td>
            <td className="px-4 py-3 text-emerald-300 text-right font-semibold tabular-nums">
              {prizeByUserId.has(row.user_id) ? formatScore(prizeByUserId.get(row.user_id)) : '-'}
            </td>
          </tr>
        )) : (
          <tr>
            <td className="px-4 py-8 text-center text-gray-400" colSpan={8}>
              Nessun dato per questa classifica
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

function CategoryRankingTable({ rows, prizeByUserId }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left border-b border-gray-600 bg-gray-700/50">
          <th className="px-4 py-3 text-gray-300 font-semibold w-16">#</th>
          <th className="px-4 py-3 text-gray-300 font-semibold">Utente</th>
          <th className="px-4 py-3 text-gray-300 font-semibold text-right">Punteggio</th>
          <th className="px-4 py-3 text-gray-300 font-semibold text-right">GW giocate</th>
          <th className="px-4 py-3 text-emerald-300 font-semibold text-right">Premio</th>
        </tr>
      </thead>
      <tbody>
        {rows.length > 0 ? rows.map((row) => (
          <tr
            key={row.user_id}
            className="border-b border-gray-700/80 hover:bg-gray-700/30 transition-colors"
          >
            <td className="px-4 py-3">
              <RankCell rank={row.rank} />
            </td>
            <td className="px-4 py-3 text-white font-medium">{row.username}</td>
            <td className="px-4 py-3 text-white text-right font-semibold tabular-nums">
              {formatScore(row.total_score)}
            </td>
            <td className="px-4 py-3 text-gray-400 text-right tabular-nums">{row.gameweeks_played}</td>
            <td className="px-4 py-3 text-emerald-300 text-right font-semibold tabular-nums">
              {prizeByUserId.has(row.user_id) ? formatScore(prizeByUserId.get(row.user_id)) : '-'}
            </td>
          </tr>
        )) : (
          <tr>
            <td className="px-4 py-8 text-center text-gray-400" colSpan={5}>
              Nessun dato per questa classifica
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

const AdminMonthlyRankings = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snapshot, setSnapshot] = useState(null);
  const [selectedPeriodKey, setSelectedPeriodKey] = useState('first');
  const [selectedView, setSelectedView] = useState('global');
  const [publishing, setPublishing] = useState(false);
  const [paying, setPaying] = useState(false);
  const [publishMessage, setPublishMessage] = useState('');
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [publishConfirmStep, setPublishConfirmStep] = useState(0);
  const [payoutPreview, setPayoutPreview] = useState(null);
  const [showPayoutPreview, setShowPayoutPreview] = useState(false);
  const [payoutConfirmStep, setPayoutConfirmStep] = useState(0);
  const [prizePools, setPrizePools] = useState({
    global: '',
    basic: '',
    plus: '',
    rare: '',
    epic: '',
    unique: '',
  });

  useEffect(() => {
    let mounted = true;

    const loadSnapshot = async () => {
      try {
        setLoading(true);
        setError('');
        setPublishMessage('');
        setShowPayoutPreview(false);
        setPayoutPreview(null);
        setPayoutConfirmStep(0);
        setShowPublishConfirm(false);
        setPublishConfirmStep(0);
        const response = await adminApi.getMonthlyRankingSnapshotByPeriod(selectedPeriodKey, 200);
        if (!mounted) return;
        setSnapshot(response?.snapshot || null);
      } catch (err) {
        if (!mounted) return;
        setError(getApiErrorMessage(err, 'Errore caricamento classifiche mensili'));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadSnapshot();
    return () => {
      mounted = false;
    };
  }, [selectedPeriodKey]);

  const period = snapshot?.period || null;
  const multipliers = snapshot?.multipliers || {};
  const categories = snapshot?.categories || {};
  const gameweeks = useMemo(
    () => (Array.isArray(snapshot?.gameweeks) ? snapshot.gameweeks : []),
    [snapshot]
  );
  const missingGameweekNumbers = useMemo(
    () => (Array.isArray(snapshot?.missingGameweekNumbers) ? snapshot.missingGameweekNumbers : []),
    [snapshot]
  );
  const globalRows = useMemo(
    () => (Array.isArray(snapshot?.global) ? snapshot.global : []),
    [snapshot]
  );

  const rowCounts = useMemo(() => {
    const counts = { global: globalRows.length };
    for (const { key } of CATEGORY_OPTIONS) {
      if (key === 'global') continue;
      counts[key] = Array.isArray(categories[key]) ? categories[key].length : 0;
    }
    return counts;
  }, [categories, globalRows]);

  const activeRows = useMemo(() => {
    if (selectedView === 'global') return globalRows;
    return Array.isArray(categories[selectedView]) ? categories[selectedView] : [];
  }, [selectedView, globalRows, categories]);

  const selectedLabel = CATEGORY_OPTIONS.find((o) => o.key === selectedView)?.label || selectedView;
  const publishedStatus = snapshot?.publishedStatus || {};
  const activePublished = publishedStatus[selectedView] || {};
  const isCategoryPublished = activePublished.published === true;
  const isCategoryPaid = activePublished.paid === true;
  const lockedPrizePool = Number(activePublished.prizePool || 0);
  const activePrizePool = isCategoryPublished
    ? lockedPrizePool
    : Number(prizePools[selectedView] || 0);
  const activeWinnerRate = selectedView === 'global' ? 0.2 : 0.15;

  useEffect(() => {
    if (!snapshot?.publishedStatus) return;
    setPrizePools((prev) => {
      const next = { ...prev };
      for (const { key } of CATEGORY_OPTIONS) {
        const st = snapshot.publishedStatus[key];
        if (st?.published && Number(st.prizePool) > 0) {
          next[key] = String(st.prizePool);
        }
      }
      return next;
    });
  }, [snapshot?.publishedStatus, selectedPeriodKey]);

  const activePrizeDistribution = useMemo(() => {
    const participants = activeRows.length;
    const slots = Math.min(countPrizeSlots(participants, activeWinnerRate), participants);
    if (participants === 0 || slots === 0 || activePrizePool <= 0) {
      return {
        participants,
        slots,
        distributionByUserId: new Map(),
        totalDistributed: 0,
      };
    }

    const winners = activeRows.slice(0, slots);
    const winnerRanks = winners.map((row, index) => (row.rank != null ? Number(row.rank) : index + 1));
    const amounts = calculateHarmonicPrizeAmounts(activePrizePool, winnerRanks);
    const distributionByUserId = new Map();
    winners.forEach((row, index) => {
      distributionByUserId.set(row.user_id, Number(amounts[index] || 0));
    });
    const totalDistributed = amounts.reduce((acc, v) => acc + Number(v || 0), 0);

    return { participants, slots, distributionByUserId, totalDistributed };
  }, [activeRows, activePrizePool, activeWinnerRate]);

  const reloadSnapshot = async () => {
    const response = await adminApi.getMonthlyRankingSnapshotByPeriod(selectedPeriodKey, 200);
    setSnapshot(response?.snapshot || null);
  };

  const handleOpenPublishConfirm = () => {
    setPublishMessage('');
    if (isCategoryPublished) {
      setPublishMessage(
        `La classifica ${selectedLabel} è già pubblicata (montepremi ${formatScore(lockedPrizePool)} DIOS). Puoi solo pagare i premi.`
      );
      return;
    }
    if (activeRows.length === 0) {
      setPublishMessage(`Nessun partecipante nella classifica ${selectedLabel}.`);
      return;
    }
    if (!Number.isFinite(activePrizePool) || activePrizePool <= 0) {
      setPublishMessage(`Inserisci un budget premio per ${selectedLabel} nella sezione "Budget premi per classifica".`);
      return;
    }
    setShowPublishConfirm(true);
    setPublishConfirmStep(1);
  };

  const handlePublishConfirmed = async () => {
    try {
      setPublishing(true);
      const response = await adminApi.publishMonthlyRanking(
        selectedPeriodKey,
        selectedView,
        activePrizePool
      );
      const result = response?.result;
      const tournamentId = result?.tournamentId;
      setPublishMessage(
        tournamentId
          ? `Classifica ${selectedLabel} pubblicata. Visibile agli utenti in /rankings (torneo #${tournamentId}).`
          : `Classifica ${selectedLabel} pubblicata con successo.`
      );
      setShowPublishConfirm(false);
      setPublishConfirmStep(0);
      await reloadSnapshot();
    } catch (err) {
      setPublishMessage(getApiErrorMessage(err, 'Errore durante la pubblicazione.'));
    } finally {
      setPublishing(false);
    }
  };

  const handleOpenPayoutPreview = async () => {
    if (!isCategoryPublished) {
      setPublishMessage(`Pubblica prima la classifica ${selectedLabel} per poter pagare i premi.`);
      return;
    }
    if (isCategoryPaid) {
      setPublishMessage(`La classifica ${selectedLabel} risulta già pagata.`);
      return;
    }
    try {
      setPaying(true);
      setPublishMessage('');
      const response = await adminApi.getMonthlyRankingPayoutPreview(selectedPeriodKey, selectedView);
      setPayoutPreview(response?.preview || null);
      setShowPayoutPreview(true);
      setPayoutConfirmStep(1);
    } catch (err) {
      setPublishMessage(getApiErrorMessage(err, 'Errore nel caricamento preview pagamenti.'));
    } finally {
      setPaying(false);
    }
  };

  const handlePayConfirmed = async () => {
    try {
      setPaying(true);
      const response = await adminApi.payMonthlyRanking(selectedPeriodKey, selectedView);
      const result = response?.result;
      setPublishMessage(
        `Pagamenti completati: ${result?.paid?.length || 0} utenti, totale ${formatScore(result?.totalPaidAmount || 0)} DIOS.`
      );
      setShowPayoutPreview(false);
      setPayoutConfirmStep(0);
      await reloadSnapshot();
    } catch (err) {
      setPublishMessage(getApiErrorMessage(err, 'Errore durante il pagamento.'));
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="admin-monthly-rankings space-y-6">
      <div className="admin-card">
        <div className="flex items-center gap-3 mb-2">
          <BarChart3 className="w-6 h-6 text-blue-400" />
          <h1 className="text-2xl font-bold text-white">Classifiche Mensili</h1>
        </div>
        <div className="mb-3 max-w-md">
          <label htmlFor="monthly-period-view" className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wide">
            Periodo mensile
          </label>
          <select
            id="monthly-period-view"
            value={selectedPeriodKey}
            onChange={(e) => setSelectedPeriodKey(e.target.value)}
            className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
          >
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.key} value={opt.key}>{opt.label}</option>
            ))}
          </select>
        </div>
        <p className="text-gray-300 text-sm">
          Somma punteggi gameweek per utente — {period?.name || 'Periodo'} ({period?.gameweekRange || '-'})
        </p>
      </div>

      {!loading && !error && gameweeks.length > 0 && (
        <div className="admin-card">
          <h2 className="text-sm font-semibold text-white mb-2">GameWeek incluse nel calcolo</h2>
          <p className="text-xs text-gray-400 mb-3">
            Filtro per range ufficiale {period?.gameweekRange || 'GW'} (indipendentemente dallo status). Si sommano i punteggi con final_score valorizzato.
          </p>
          <div className="flex flex-wrap gap-2">
            {gameweeks.map((gw) => (
              <span
                key={gw.id}
                className="text-xs px-2.5 py-1 rounded-full border border-blue-500/30 bg-blue-500/10 text-blue-200"
                title={`DB id ${gw.id} · status ${gw.status}`}
              >
                #{gw.number} {gw.name}
              </span>
            ))}
          </div>
          {missingGameweekNumbers.length > 0 && (
            <p className="text-amber-300 text-xs mt-3">
              Mancanti in DB nel range: GameWeek #{missingGameweekNumbers.join(', #')}
            </p>
          )}
        </div>
      )}

      <div className="admin-card">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <div className="bg-black/20 border border-white/10 rounded-lg p-3">
            <div className="text-gray-400 text-xs uppercase tracking-wide">Periodo</div>
            <div className="text-white font-semibold mt-1">{period?.name || '-'}</div>
          </div>
          <div className="bg-black/20 border border-white/10 rounded-lg p-3">
            <div className="text-gray-400 text-xs uppercase tracking-wide">Gameweek</div>
            <div className="text-white font-semibold mt-1">{period?.gameweekRange || '-'}</div>
          </div>
          <div className="bg-black/20 border border-white/10 rounded-lg p-3">
            <div className="text-gray-400 text-xs uppercase tracking-wide">Intervallo</div>
            <div className="text-white font-semibold mt-1">
              {toItDate(period?.startDate)} – {toItDate(period?.endDate)}
            </div>
          </div>
        </div>
      </div>

      {!loading && !error && (
        <div className="admin-card">
          <h2 className="text-sm font-semibold text-white mb-3">Budget premi per classifica</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {CATEGORY_OPTIONS.map((opt) => {
              const catPublished = publishedStatus[opt.key]?.published === true;
              const catPaid = publishedStatus[opt.key]?.paid === true;
              return (
              <label key={opt.key} className={`rounded-lg p-3 border ${
                catPublished ? 'bg-emerald-900/20 border-emerald-500/30' : 'bg-black/20 border-white/10'
              }`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="text-xs text-gray-400 uppercase tracking-wide">{opt.label}</div>
                  {catPublished && (
                    <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${
                      catPaid ? 'bg-blue-500/20 text-blue-300' : 'bg-emerald-500/20 text-emerald-300'
                    }`}>
                      {catPaid ? 'Pagata' : 'Pubblicata'}
                    </span>
                  )}
                </div>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={prizePools[opt.key] ?? ''}
                  onChange={(e) => setPrizePools((prev) => ({ ...prev, [opt.key]: e.target.value }))}
                  placeholder="0.00"
                  disabled={catPublished}
                  readOnly={catPublished}
                  title={catPublished ? 'Montepremi bloccato dopo la pubblicazione' : undefined}
                  className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                    catPublished
                      ? 'bg-gray-900/80 border-gray-700 text-gray-400 cursor-not-allowed focus:ring-0'
                      : 'bg-gray-800 border-gray-600 text-white focus:ring-emerald-500/50'
                  }`}
                />
              </label>
            );
            })}
          </div>
        </div>
      )}

      {loading && (
        <div className="admin-card flex flex-col items-center py-12 text-gray-300">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-400 border-t-transparent" />
          <p className="mt-4">Caricamento classifiche...</p>
        </div>
      )}

      {error && (
        <div className="admin-card text-red-300 border border-red-500/30 bg-red-900/20">{error}</div>
      )}

      {!loading && !error && (
        <div className="admin-card p-0 overflow-hidden border border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 p-4 sm:p-5 border-b border-gray-700 bg-gray-800/80">
            <div className="flex items-start gap-3">
              <Trophy className="w-6 h-6 text-amber-400 shrink-0 mt-0.5" />
              <div>
                <h2 className="text-lg font-semibold text-white">Classifica {selectedLabel}</h2>
                <p className="text-gray-400 text-sm mt-0.5">
                  {activeRows.length} {activeRows.length === 1 ? 'partecipante' : 'partecipanti'}
                </p>
                <p className="text-emerald-300/90 text-xs mt-1">
                  Budget: {formatScore(activePrizePool)} DIOS
                  {isCategoryPublished && (
                    <span className="text-amber-300/90"> (bloccato)</span>
                  )}
                  {' '}· Premiati: {activePrizeDistribution.slots} ({Math.round(activeWinnerRate * 100)}%)
                </p>
                {isCategoryPublished && (
                  <p className="text-emerald-300/80 text-xs mt-1">
                    {isCategoryPaid
                      ? 'Classifica pubblicata e pagata — nessuna modifica consentita.'
                      : 'Classifica pubblicata — conferma e montepremi non modificabili. Usa «Paga» per i premi.'}
                  </p>
                )}
              </div>
            </div>

            <div className="sm:min-w-[220px]">
              <label htmlFor="monthly-ranking-view" className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wide">
                Mostra classifica
              </label>
              <select
                id="monthly-ranking-view"
                value={selectedView}
                onChange={(e) => {
                  setSelectedView(e.target.value);
                  setShowPublishConfirm(false);
                  setPublishConfirmStep(0);
                  setShowPayoutPreview(false);
                  setPayoutPreview(null);
                  setPayoutConfirmStep(0);
                }}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
              >
                {CATEGORY_OPTIONS.map((opt) => (
                  <option key={opt.key} value={opt.key}>
                    {opt.label} ({rowCounts[opt.key] ?? 0})
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:min-w-[220px]">
              <button
                type="button"
                onClick={handleOpenPublishConfirm}
                disabled={publishing || paying || activeRows.length === 0 || isCategoryPublished}
                className={`w-full px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                  publishing || paying || activeRows.length === 0 || isCategoryPublished
                    ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'bg-emerald-600 hover:bg-emerald-500 text-white'
                }`}
              >
                {isCategoryPublished ? `${selectedLabel} pubblicata` : `Conferma ${selectedLabel}`}
              </button>
            </div>
            <div className="sm:min-w-[220px]">
              <button
                type="button"
                onClick={handleOpenPayoutPreview}
                disabled={paying || publishing || !isCategoryPublished || isCategoryPaid}
                className={`w-full px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                  paying || publishing || !isCategoryPublished || isCategoryPaid
                    ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-500 text-white'
                }`}
              >
                {paying ? 'Carico preview...' : isCategoryPaid ? `${selectedLabel} pagata` : `Paga ${selectedLabel}`}
              </button>
            </div>
          </div>

          {publishMessage && !showPublishConfirm && !showPayoutPreview && (
            <div
              className={`mx-4 sm:mx-5 mb-4 rounded-lg border px-3 py-2 text-sm ${
                publishMessage.toLowerCase().includes('errore')
                || publishMessage.toLowerCase().includes('inserisci')
                || publishMessage.toLowerCase().includes('nessun')
                  ? 'text-amber-200 border-amber-500/40 bg-amber-900/20'
                  : 'text-emerald-300 border-emerald-500/30 bg-emerald-900/10'
              }`}
            >
              {publishMessage}
            </div>
          )}

          {selectedView === 'global' && (
            <p className="px-4 sm:px-5 py-2 text-xs text-gray-500 border-b border-gray-700/80 bg-gray-900/40 font-mono">
              Plus×{multipliers.plus ?? 1} + Rare×{multipliers.rare ?? 1.25} + Epiche×{multipliers.epic ?? 1.5} + Unique×{multipliers.unique ?? 2} (Basic esclusa)
            </p>
          )}

          <div className="overflow-x-auto max-h-[min(70vh,640px)] overflow-y-auto bg-gray-900/70">
            {selectedView === 'global' ? (
              <GlobalRankingTable
                rows={activeRows}
                multipliers={multipliers}
                prizeByUserId={activePrizeDistribution.distributionByUserId}
              />
            ) : (
              <CategoryRankingTable
                rows={activeRows}
                prizeByUserId={activePrizeDistribution.distributionByUserId}
              />
            )}
          </div>
        </div>
      )}

      {showPublishConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div
            className="bg-gray-800 rounded-xl border border-emerald-500/40 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="monthly-publish-confirm-title"
          >
            <div className="p-4 border-b border-gray-700">
              <h3 id="monthly-publish-confirm-title" className="text-white font-semibold text-lg">
                Conferma pubblicazione — {selectedLabel}
              </h3>
              <p className="text-xs text-gray-400 mt-2">
                Periodo: {period?.name} ({period?.gameweekRange}) · Partecipanti: {activePrizeDistribution.participants} ·
                Premiati: {activePrizeDistribution.slots} ({Math.round(activeWinnerRate * 100)}%) ·
                Budget: {formatScore(activePrizePool)} DIOS
              </p>
            </div>
            {activePrizeDistribution.slots > 0 && (
              <div className="overflow-x-auto overflow-y-auto flex-1 min-h-0 bg-gray-900/60 border-y border-gray-700">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10">
                    <tr className="text-left border-b border-gray-700 bg-gray-800/95">
                      <th className="px-3 py-2 text-gray-300">Rank</th>
                      <th className="px-3 py-2 text-gray-300">Utente</th>
                      <th className="px-3 py-2 text-gray-300 text-right">Score</th>
                      <th className="px-3 py-2 text-emerald-300 text-right">Premio previsto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeRows.slice(0, activePrizeDistribution.slots).map((row) => (
                      <tr key={row.user_id} className="border-b border-gray-800">
                        <td className="px-3 py-2 text-white">#{row.rank}</td>
                        <td className="px-3 py-2 text-white">{row.username}</td>
                        <td className="px-3 py-2 text-gray-300 text-right">
                          {formatScore(selectedView === 'global' ? row.weighted_score : row.total_score)}
                        </td>
                        <td className="px-3 py-2 text-emerald-300 text-right font-semibold">
                          {formatScore(activePrizeDistribution.distributionByUserId.get(row.user_id) || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="p-4 flex flex-wrap gap-2">
              {publishConfirmStep === 1 && (
                <button
                  type="button"
                  onClick={() => setPublishConfirmStep(2)}
                  className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm font-semibold"
                >
                  Prima conferma
                </button>
              )}
              {publishConfirmStep === 2 && (
                <button
                  type="button"
                  onClick={handlePublishConfirmed}
                  disabled={publishing}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold ${
                    publishing ? 'bg-gray-700 text-gray-400' : 'bg-emerald-600 hover:bg-emerald-500 text-white'
                  }`}
                >
                  {publishing ? 'Pubblicazione...' : 'Seconda conferma: pubblica ora'}
                </button>
              )}
              <button
                type="button"
                onClick={() => { setShowPublishConfirm(false); setPublishConfirmStep(0); }}
                disabled={publishing}
                className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm font-semibold"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      {showPayoutPreview && payoutPreview && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div
            className="bg-gray-800 rounded-xl border border-blue-500/40 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="monthly-payout-confirm-title"
          >
            <div className="p-4 border-b border-gray-700">
              <h3 id="monthly-payout-confirm-title" className="text-white font-semibold text-lg">
                Preview pagamenti DIOS — {selectedLabel}
              </h3>
              <p className="text-xs text-gray-400 mt-2">
                Torneo #{payoutPreview.tournamentId} · Vincitori: {payoutPreview.winners} · Totale: {formatScore(payoutPreview.totalAmount)} DIOS
              </p>
            </div>
            <div className="overflow-x-auto overflow-y-auto flex-1 min-h-0 bg-gray-900/60 border-y border-gray-700">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10">
                  <tr className="text-left border-b border-gray-700 bg-gray-800/95">
                    <th className="px-3 py-2 text-gray-300">Rank</th>
                    <th className="px-3 py-2 text-gray-300">Utente</th>
                    <th className="px-3 py-2 text-gray-300 text-right">Score</th>
                    <th className="px-3 py-2 text-emerald-300 text-right">DIOS</th>
                  </tr>
                </thead>
                <tbody>
                  {(payoutPreview.payouts || []).map((p) => (
                    <tr key={`${p.userId}-${p.rank}`} className="border-b border-gray-800">
                      <td className="px-3 py-2 text-white">#{p.rank}</td>
                      <td className="px-3 py-2 text-white">{p.username}</td>
                      <td className="px-3 py-2 text-gray-300 text-right">{formatScore(p.score)}</td>
                      <td className="px-3 py-2 text-emerald-300 text-right font-semibold">{formatScore(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-4 flex flex-wrap gap-2">
              {payoutConfirmStep === 1 && (
                <button
                  type="button"
                  onClick={() => setPayoutConfirmStep(2)}
                  className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm font-semibold"
                >
                  Prima conferma
                </button>
              )}
              {payoutConfirmStep === 2 && (
                <button
                  type="button"
                  onClick={handlePayConfirmed}
                  disabled={paying}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold ${
                    paying ? 'bg-gray-700 text-gray-400' : 'bg-red-600 hover:bg-red-500 text-white'
                  }`}
                >
                  {paying ? 'Pagamento in corso...' : 'Seconda conferma: paga ora'}
                </button>
              )}
              <button
                type="button"
                onClick={() => { setShowPayoutPreview(false); setPayoutConfirmStep(0); }}
                disabled={paying}
                className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm font-semibold"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      {publishMessage && (showPublishConfirm || showPayoutPreview) && (
        <div
          className={`admin-card text-sm border ${
            publishMessage.toLowerCase().includes('errore')
            || publishMessage.toLowerCase().includes('inserisci')
            || publishMessage.toLowerCase().includes('nessun')
              ? 'text-amber-200 border-amber-500/40 bg-amber-900/20'
              : 'text-emerald-300 border-emerald-500/30'
          }`}
        >
          {publishMessage}
        </div>
      )}

      <p className="text-xs text-gray-500 px-1">
        Fonte: tornei gameweek del range {period?.gameweekRange || 'GW'} (qualsiasi status, con final_score). Premio: top 15% (20% globale) con ripartizione armonica gameweek.
      </p>
    </div>
  );
};

export default AdminMonthlyRankings;
